import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';

import Card from 'components/Card';
import Modal from 'components/Modal';
import Text from 'components/Text';
import Heading from 'components/Heading';
import Panel from 'components/Panel';
import ModalHeadline from 'components/ModalHeadline';
import Button from 'components/Button';
import StickyButtonPanel from 'components/StickyButtonPanel';
import Stack from 'components/Stack';

import DEFAULT_SCORM_SETTINGS from './defaultScormSettings';

import {
	getCreditOptions,
	navigationTypeOptions,
	openContentInOptions,
	masteryScoreOptions,
	scoreRollupActionOptions,
	statusRollupActionOptions,
} from './options';
import {Option} from './option';
import {getPopupWindowParam, setPopupWindowParam} from './popupWindowFunctions';

function SectionHeading({children, description}) {
	return (
		<Panel>
			<Heading as="h3">{children}</Heading>
			{description && (
				<Text as="p" dimmed mt="s">
					{description}
				</Text>
			)}
		</Panel>
	);
}

function ScormAdvancedSettingsModal({
	isButtonDisabled = false,
	defaultSettings,
	onClose,
	onChange,
	onSave,
}) {
	const {t} = useTranslation();
	const [settings, setSettings] = useState({
		...DEFAULT_SCORM_SETTINGS,
		...defaultSettings,
	});

	const boolOptionsMapping = {
		getCredit: getCreditOptions,
		openContentIn: openContentInOptions,
	};

	function getBooleanValue(selectedValue, mapping) {
		const selectedOption = mapping.find(
			option => option.value === selectedValue
		);
		return selectedOption.bool;
	}

	function handleChange(name, value) {
		const updatedSettings = {...settings, [name]: value};
		setSettings(updatedSettings);
		onChange(updatedSettings);
	}

	function handleChangeBoolConvert(name, value) {
		const mapping = boolOptionsMapping[name];
		const newValue = mapping.find(option => option.bool === value);
		handleChange(name, newValue.value);
	}

	function handleChangePopupWindowParams(name, value) {
		const oldConfig = settings.popupWindowParams;
		const newConfig = setPopupWindowParam(oldConfig, name, value);
		handleChange('popupWindowParams', newConfig);
	}

	const titleId = 'advanced-scorm-settings-title';

	const handleSave = e => {
		e.stopPropagation();
		onSave();
	};

	return (
		<Modal
			id="advanced-scorm"
			width="wide"
			aria-labelledby={titleId}
			onRequestClose={onClose}
		>
			<Card variant="raised" overflow="visible">
				<ModalHeadline
					sticky
					title={t('scorm_settings_title')}
					titleId={titleId}
					onClose={onClose}
				/>
				<Panel>
					<p>{t('scorm_settings_info')}</p>
				</Panel>

				<Stack withDividers>
					<>
						<SectionHeading>
							{t('scorm_general_title')}
						</SectionHeading>

						<Option
							type="switch"
							id="openContentIn"
							title={t('scorm_general_option_title_open_content')}
							description={t(
								'scorm_general_option_info_open_content'
							)}
							value={getBooleanValue(
								settings.openContentIn,
								openContentInOptions
							)}
							onChange={handleChangeBoolConvert}
						/>
						<Option
							id="height"
							title={t('scorm_general_option_title_height')}
							description={t('scorm_general_option_info_height')}
							descriptionType="tooltip"
							value={getPopupWindowParam(
								settings.popupWindowParams,
								'height'
							)}
							type="number"
							indented={true}
							disabled={
								!getBooleanValue(
									settings.openContentIn,
									openContentInOptions
								)
							}
							onChange={handleChangePopupWindowParams}
						/>
						<Option
							id="width"
							title={t('scorm_general_option_title_width')}
							description={t('scorm_general_option_info_width')}
							descriptionType="tooltip"
							value={getPopupWindowParam(
								settings.popupWindowParams,
								'width'
							)}
							type="number"
							indented={true}
							disabled={
								!getBooleanValue(
									settings.openContentIn,
									openContentInOptions
								)
							}
							onChange={handleChangePopupWindowParams}
						/>
						<Option
							type="switch"
							id="allowNewAttemptWhenReopeningNewCourse"
							title={t(
								'scorm_general_option_title_multiple_attempts'
							)}
							description={t(
								'scorm_general_option_info_multiple_attempts'
							)}
							value={
								settings.allowNewAttemptWhenReopeningNewCourse
							}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="getCredit"
							title={t('scorm_general_option_title_get_credit')}
							value={getBooleanValue(
								settings.getCredit,
								getCreditOptions
							)}
							onChange={handleChangeBoolConvert}
						/>
						<Option
							type="switch"
							id="reenterActivity"
							title={t(
								'scorm_general_option_title_reenter_activity'
							)}
							description={t(
								'scorm_general_option_info_reenter_activity'
							)}
							value={settings.reenterActivity}
							onChange={handleChange}
						/>
						<Option
							type="text"
							id="commentsFromLMS"
							title={t(
								'scorm_general_option_title_comments_from_lms'
							)}
							description={t(
								'scorm_general_option_info_comments_from_lms'
							)}
							descriptionType="tooltip"
							value={settings.commentsFromLMS}
							onChange={handleChange}
						/>
					</>

					<>
						<SectionHeading description={t('scorm_status_info')}>
							{t('scorm_status_title')}
						</SectionHeading>

						<Option
							type="switch"
							id="statusAllowChangeAfterCompleted"
							title={t(
								'scorm_status_option_title_change_after_completed'
							)}
							description={t(
								'scorm_status_option_info_change_after_completed'
							)}
							value={settings.statusAllowChangeAfterCompleted}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="statusAllowChangeAfterFailed"
							title={t(
								'scorm_status_option_title_change_after_failed'
							)}
							description={t(
								'scorm_status_option_info_change_after_failed'
							)}
							value={settings.statusAllowChangeAfterFailed}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="statusAllowChangeAfterPassed"
							title={t(
								'scorm_status_option_title_change_after_passed'
							)}
							description={t(
								'scorm_status_option_info_change_after_passed'
							)}
							value={settings.statusAllowChangeAfterPassed}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="failedStatusCountsAsDone"
							title={t(
								'scorm_status_option_title_count_failed_as_done'
							)}
							description={t(
								'scorm_status_option_info_count_failed_as_done'
							)}
							value={settings.failedStatusCountsAsDone}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="startIncomplete"
							title={t(
								'scorm_status_option_title_start_incomplete'
							)}
							description={t(
								'scorm_status_option_info_start_incomplete'
							)}
							value={settings.startIncomplete}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="statusAllowCompletionOnUnsetFinish"
							title={t('scorm_status_option_title_autocomplete')}
							description={t(
								'scorm_status_option_info_autocomplete'
							)}
							value={settings.statusAllowCompletionOnUnsetFinish}
							onChange={handleChange}
						/>
						<Option
							type="select"
							id="masteryScoreMode"
							title={t('scorm_status_option_title_mastery_score')}
							description={t(
								'scorm_status_option_info_mastery_score'
							)}
							descriptionType="tooltip"
							options={masteryScoreOptions()}
							value={settings.masteryScoreMode}
							onChange={handleChange}
						/>
					</>

					<>
						<SectionHeading description={t('scorm_score_info')}>
							{t('scorm_score_title')}
						</SectionHeading>

						<Option
							type="switch"
							id="scoreAllowChangeAfterCompleted"
							title={t(
								'scorm_score_option_title_change_after_completed'
							)}
							description={t(
								'scorm_score_option_info_change_after_completed'
							)}
							value={settings.scoreAllowChangeAfterCompleted}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="scoreAllowChangeAfterFailed"
							title={t(
								'scorm_score_option_title_change_after_failed'
							)}
							description={t(
								'scorm_score_option_info_change_after_failed'
							)}
							value={settings.scoreAllowChangeAfterFailed}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="scoreAllowChangeAfterPassed"
							title={t(
								'scorm_score_option_title_change_after_passed'
							)}
							description={t(
								'scorm_score_option_info_change_after_passed'
							)}
							value={settings.scoreAllowChangeAfterPassed}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="scoreAllowReduce"
							title={t(
								'scorm_score_option_title_score_allow_reduced'
							)}
							description={t(
								'scorm_score_option_info_score_allow_reduced'
							)}
							value={settings.scoreAllowReduce}
							onChange={handleChange}
						/>
					</>

					<>
						<SectionHeading description={t('scorm_multi_info')}>
							{t('scorm_multi_title')}
						</SectionHeading>

						<Option
							type="switch"
							id="autoLaunchFirstSco"
							title={t(
								'scorm_multi_option_title_autolaunch_first_sco'
							)}
							description={t(
								'scorm_multi_option_info_autolaunch_first_sco'
							)}
							value={settings.autoLaunchFirstSco}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="returnToLastScoOnLaunch"
							title={t(
								'scorm_multi_option_title_return_last_sco_launch'
							)}
							description={t(
								'scorm_multi_option_info_return_last_sco_launch'
							)}
							value={settings.returnToLastScoOnLaunch}
							onChange={handleChange}
						/>
						<Option
							type="select"
							id="navigationType"
							title={t(
								'scorm_multi_option_title_navigation_type'
							)}
							description={t(
								'scorm_multi_option_info_navigation_type'
							)}
							descriptionType="tooltip"
							options={navigationTypeOptions()}
							value={settings.navigationType}
							onChange={handleChange}
						/>
						<Option
							type="select"
							id="statusRollupAction"
							title={t(
								'scorm_multi_option_title_status_rollup_action'
							)}
							description={t(
								'scorm_multi_option_info_status_rollup_action'
							)}
							descriptionType="tooltip"
							options={statusRollupActionOptions()}
							value={settings.statusRollupAction}
							onChange={handleChange}
						/>
						<Option
							type="select"
							id="scoreRollupAction"
							title={t(
								'scorm_multi_option_title_score_rollup_action'
							)}
							description={t(
								'scorm_multi_option_info_score_rollup_action'
							)}
							descriptionType="tooltip"
							options={scoreRollupActionOptions()}
							value={settings.scoreRollupAction}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="scoreRollupIncludeScosWithNoScore"
							title={t(
								'scorm_multi_option_title_score_rollup_include_sco'
							)}
							description={t(
								'scorm_multi_option_info_score_rollup_include_sco'
							)}
							value={settings.scoreRollupIncludeScosWithNoScore}
							onChange={handleChange}
						/>
					</>

					<>
						<SectionHeading description={t('scorm_data_info')}>
							{t('scorm_data_title')}
						</SectionHeading>

						<Option
							type="switch"
							id="saveDataOnCommit"
							title={t('scorm_data_option_title_save_on_commit')}
							description={t(
								'scorm_data_option_info_save_on_commit'
							)}
							value={settings.saveDataOnCommit}
							onChange={handleChange}
						/>
						<Option
							type="switch"
							id="saveDataOnSetValue"
							title={t(
								'scorm_data_option_title_save_on_set_value'
							)}
							description={t(
								'scorm_data_option_info_save_on_set_value'
							)}
							value={settings.saveDataOnSetValue}
							onChange={handleChange}
						/>
					</>
				</Stack>
				<StickyButtonPanel>
					<Button
						type="submit"
						variant="primary"
						onClick={handleSave}
						isDisabled={isButtonDisabled}
					>
						{t('actions_save_and_close')}
					</Button>
				</StickyButtonPanel>
			</Card>
		</Modal>
	);
}

export default ScormAdvancedSettingsModal;
